import { createContext, PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEffectOnce } from 'react-use';
import { useIsMutating } from '@tanstack/react-query';
import { isNil } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import { parseAsInteger, useQueryState } from 'nuqs';
import { useAuth } from '@zep/auth';
import { SpacePlanPaymentPriceInfo, SpaceSimpleDto } from '@zep/types';
import { PlanCheckoutKeys } from '../../../../consts';
import { useGetExpectPlanPrice } from '../../../../services';
import { Currency, PlanProduct, SpaceBillingInfo, SpacePlan, SpacePlanDto, SpacePlanProducts, SpacePlanSubscriptionDto, SubscribePlan } from '../../../../types';
import { fCurrency, fDateByCountryCode } from '../../../../utils';
import { getVatPreview } from '../../plan-payment/utils/vatUtils';
import { capitalizeFirstLetter, getSubscriptionPlanLevel } from '../subscriptionUtil';
import { useGetPlanInfo } from './hooks/useGetPlanInfo';
import { SubscriptionPaymentLogPayload } from './hooks';
export const SubscriptionPaymentContext = createContext<PlanCheckoutContextProps | undefined>(undefined);
export const SubscriptionPaymentProvider = ({
  children,
  plan: initPlan,
  maxCcu: initMaxCcu,
  spacePlanProducts,
  billingInfo,
  spaceHashId,
  planInfo: initPlanInfo,
  showEnterpriseSample,
  subscription,
  space,
  spacePlan,
  isKR,
  isUpdated,
  subscriptionRequestHashId,
  customSubscriptionRequestPriceInfo
}: SubscriptionPaymentProviderProps) => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    user
  } = useAuth();
  const [step, setStep] = useState<Step>(1);
  const timeoutRef = useRef(null);
  const currency: Currency = isKR ? 'KRW' : 'USD';
  const [selectedPlan, setSubscribePlan] = useState<Omit<SubscribePlan, 'free'>>(initPlan ?? 'basic');
  const [spaceBillingInfo, setSpaceBillingInfo] = useState<SpaceBillingInfo>(
  // @ts-ignore
  billingInfo);
  const isLoadingRegisterCard = useIsMutating({
    mutationKey: [PlanCheckoutKeys.RegisterCard]
  });
  const isLoadingCheckout = useIsMutating({
    mutationKey: [PlanCheckoutKeys.Checkout]
  });
  // 결제 정보 업데이트 유무
  const [isUpdatePayment, setIsUpdatedPayment] = useState(false);
  // 결제 전송 로딩 상태
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  // 카드가 해외 pg사 혹은, zep api 로 등록 성공 여부 파악
  const [isRegisterCard, setIsRegisterCard] = useState(!!billingInfo);
  const [selectedMaxCcu, setSelectedMaxCcu] = useState<number>(initMaxCcu);
  const [subscriptionPeriodQuery, setSubscriptionPeriodQuery] = useQueryState('subscriptionPeriod', parseAsInteger.withDefault(365));
  const [selectedPlanInfo, setSelectedPlanInfo] = useState(initPlanInfo);
  const getPlanInfo = useGetPlanInfo({
    currency,
    isAnnuallyPayment: subscriptionPeriodQuery === 365,
    customSubscriptionRequestPriceInfo
  });
  const currencyString = t('subscription:currency.won');
  const isAnnuallyPayment = subscriptionPeriodQuery === 365;
  const {
    data: expectedPriceRes
  } = useGetExpectPlanPrice(spaceHashId, selectedPlanInfo.id, {
    enabled: isUpdated,
    queryKey: [...PlanCheckoutKeys.ExpectedPrice(spaceHashId, selectedPlanInfo.id)],
    placeholderData: () => {
      return {
        data: {
          isUpgrade: false,
          asisPlanId: 0,
          spacePlanTemplateId: selectedPlanInfo.id,
          saleType: selectedPlanInfo?.saleType ?? '',
          salePrice: 0,
          unusedPrice: 0,
          unusedDayCount: 0,
          saleDiscountPrice: selectedPlanInfo.saleDiscountPrice,
          totalDiscountPrice: selectedPlanInfo.saleDiscountPrice,
          finalPrice: selectedPlanInfo.finalPrice,
          originalPrice: selectedPlanInfo.originalPrice,
          vatAmount: 0,
          vatRate: 0,
          priceBeforeVat: 0
        },
        status: 0
      };
    }
  });
  const handleIsRegisterCard = useCallback((_isRegisterCard: boolean) => {
    setIsRegisterCard(_isRegisterCard);
  }, []);
  const handleSelectedPlan = useCallback((plan: SubscribePlan) => {
    setSubscribePlan(plan);
  }, []);
  const handleSelectedMaxCcu = useCallback((count: number) => {
    setSelectedMaxCcu(count);
  }, []);
  const handleIsUpdatedPayment = useCallback((_isUpdated: boolean) => {
    setIsUpdatedPayment(_isUpdated);
  }, []);
  const handleStep = useCallback((_step: Step) => {
    setStep(_step);
  }, []);
  const handleSpaceBilling = useCallback((_spaceBillingInfo: Partial<SpaceBillingInfo>) => {
    setSpaceBillingInfo(prev => ({
      ...prev,
      ..._spaceBillingInfo
    }));
  }, []);
  const updateStatus: PaymentUpdateStatus = useMemo(() => {
    if (!isUpdated || !spacePlan) {
      return {
        type: 'NONE_UPGRADE',
        messages: [''],
        enabledSubmit: true
      };
    }
    const {
      maxCcu: nowMaxCcu,
      planType
    } = spacePlan;
    const {
      unusedPrice: 미사용금액,
      finalPrice: 추가결제예상금액,
      unusedDayCount
    } = expectedPriceRes.data;
    const type = ((): PaymentUpdateStatusType => {
      // @ts-ignore
      const selectedPlanLevel: number = getSubscriptionPlanLevel(selectedPlan);
      const nowPlanLevel: number = getSubscriptionPlanLevel(planType);
      const isSameGrade = selectedPlanLevel === nowPlanLevel && selectedMaxCcu === nowMaxCcu;
      if (subscription.nextSpacePlanTemplateId === selectedPlanInfo.id) {
        return 'RESERVED_GRADE';
      }
      if (isSameGrade) {
        return 'SAME_GRADE';
      }

      // 최우선순위
      if (selectedMaxCcu > nowMaxCcu) {
        return 'UP_GRADE';
      }
      return selectedPlanLevel > nowPlanLevel ? 'UP_GRADE' : 'DOWN_GRADE';
    })();
    const nowPlan = capitalizeFirstLetter(planType);
    switch (type) {
      case 'UP_GRADE':
        {
          return {
            type,
            enabledSubmit: true,
            messages: 추가결제예상금액 <= 0 ? [t('subscription:payment.summary.upgradeNonePrice')] : [t('subscription:payment.summaryUpgrade.priceGuideText', {
              prevCcu: nowMaxCcu < 0 ? t('subscription:SpaceList.unlimitedCcu') : nowMaxCcu,
              prevPlan: nowPlan,
              subscriptionPeriod: subscription.subscriptionPeriod,
              leftDays: unusedDayCount,
              leftPrice: fCurrency(미사용금액, currency, {
                currencyString
              }),
              price: fCurrency(추가결제예상금액, currency, {
                currencyString
              })
            })]
          };
        }
      case 'DOWN_GRADE':
        {
          const 다음결제예상일 = fDateByCountryCode(
          // @ts-ignore
          !isNil(subscription?.nextCreationDate) && subscription?.nextCreationDate, space.countryCode);
          return {
            type,
            enabledSubmit: true,
            messages: [t('subscription:payment.summaryDowngrade.endDateGuideText', {
              prevCcu: nowMaxCcu < 0 ? t('subscription:SpaceList.unlimitedCcu') : nowMaxCcu,
              prevPlan: capitalizeFirstLetter(nowPlan),
              endDate: 다음결제예상일
            }), t('subscription:payment.summaryDowngrade.timingGuideText', {
              ccu: selectedMaxCcu < 0 ? t('subscription:SpaceList.unlimitedCcu') : selectedMaxCcu,
              plan: capitalizeFirstLetter(selectedPlan),
              appliedDate: 다음결제예상일
            })]
          };
        }
      case 'RESERVED_GRADE':
        {
          return {
            type: 'RESERVED_GRADE',
            enabledSubmit: false,
            messages: [t('subscription:payment.summary.reservedPlanGuideText', {
              date: fDateByCountryCode(spacePlan?.endDate, space.countryCode)
            })]
          };
        }
      case 'SAME_GRADE':
      default:
        {
          return {
            type: 'SAME_GRADE',
            enabledSubmit: false,
            messages: [t('subscription:payment.summary.samegrade')]
          };
        }
    }
  }, [isUpdated, spacePlan, expectedPriceRes.data, selectedPlan, selectedMaxCcu, subscription?.subscriptionPeriod, subscription?.nextSpacePlanTemplateId, subscription?.nextCreationDate, selectedPlanInfo?.id, t, currency, currencyString, space.countryCode]);
  const 추가결제금액 = useMemo<{
    value: number;
    localString: string;
  } | undefined>(() => {
    if (!isUpdated || !expectedPriceRes.data) return undefined;
    const morePrice = expectedPriceRes.data.finalPrice > 0 ? expectedPriceRes.data.finalPrice : 0;
    return {
      value: morePrice,
      localString: fCurrency(morePrice, currency, {
        currencyString
      })
    };
  }, [isUpdated, expectedPriceRes.data, currency, currencyString]);
  const amplitudeData: SubscriptionPaymentLogPayload = (() => {
    // 기존 코드는 ?? 와 삼항 우선순위 문제로 spacePlan.currency가 'USD'여도
    // 'KRW'로 평가되어 revenue 환산이 오염됐다. 괄호로 의도를 고정한다.
    const resolvedCurrency = spacePlan?.currency ?? (space.countryCode === 'KR' ? 'KRW' : 'USD');
    let price = 0;
    switch (updateStatus.type) {
      case 'UP_GRADE':
        {
          // 추가 금액만 price — 서버 expected finalPrice(서버 VAT 합류 시 VAT 포함)
          price = expectedPriceRes?.data?.finalPrice || 0;
          break;
        }
      case 'NONE_UPGRADE':
        {
          if (selectedPlanInfo.priceInfo) {
            price = selectedPlanInfo.priceInfo.finalPrice;
            break;
          }

          // 신규 결제 — revenue는 실결제액 기준이므로 결제 버튼/PG와 동일하게
          // VAT preview를 포함한 최종 금액을 사용한다.
          price = getVatPreview({
            amount: selectedPlanInfo.finalPrice,
            currency: resolvedCurrency,
            userCountryCode: user?.countryCode,
            scenario: 'NEW_SUBSCRIPTION_FIRST_PAYMENT'
          }).finalPrice;
          break;
        }
      default:
        price = 0;
    }
    return {
      selectedPlan: selectedPlan,
      selectedCcu: selectedMaxCcu,
      nowCcu: spacePlan?.maxCcu,
      nowPlan: spacePlan?.planType.toLowerCase(),
      price,
      spaceHashId: space.hashId,
      currency: resolvedCurrency,
      countryCode: space.countryCode,
      spaceName: space.name
    };
  })();
  useEffect(() => {
    if (isLoadingRegisterCard > 0 || isLoadingCheckout > 0) {
      setIsSubmitLoading(true);
      return;
    }

    // 자연스럽게 0.5초 정도 고의로 딜레이줌
    if (isLoadingRegisterCard === 0 && isLoadingCheckout === 0) {
      // @ts-ignore
      timeoutRef.current = setTimeout(() => {
        setIsSubmitLoading(false);
      }, 500);
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isLoadingRegisterCard, isLoadingCheckout]);
  useEffect(() => {
    setSelectedPlanInfo(getPlanInfo({
      plans: spacePlanProducts,
      planType: selectedPlan,
      maxCcu: selectedMaxCcu
    }));
  }, [selectedMaxCcu, selectedPlan, spacePlanProducts, currency, getPlanInfo]);
  useEffectOnce(() => {
    if (subscription?.subscriptionPeriod) {
      setSubscriptionPeriodQuery(subscription?.subscriptionPeriod);
    }
  });
  return <SubscriptionPaymentContext.Provider value={{
    subscription,
    isKR,
    currency,
    selectedPlan,
    selectedPlanInfo,
    step,
    spacePlanProducts,
    showEnterpriseSample,
    spaceHashId,
    selectedMaxCcu,
    isUpdated,
    isUpdatePayment,
    isRegisterCard,
    isSubmitLoading,
    space,
    추가결제금액,
    spacePlan,
    billingInfo: spaceBillingInfo,
    // @ts-ignore
    handleSelectedPlan,
    handleSelectedMaxCcu,
    handleIsUpdatedPayment,
    handleIsRegisterCard,
    handleSpaceBilling,
    handleStep,
    updateStatus,
    amplitudeData,
    isAnnuallyPayment,
    subscriptionPeriod: subscriptionPeriodQuery,
    subscriptionRequestHashId,
    handlePaymentPeriod: setSubscriptionPeriodQuery,
    // 연간 결제에만 유효
    annualSaleRate:
    // 수동 결제 케이스

    // @ts-ignore
    Object.keys(spacePlanProducts).length === 1 ?
    // @ts-ignore
    spacePlanProducts[Object.keys(spacePlanProducts)[0]][
    // @ts-ignore
    spacePlanProducts[Object.keys(spacePlanProducts)[0]].length - 1].saleDiscountRate : spacePlanProducts.BASIC[spacePlanProducts.BASIC.length - 1].saleDiscountRate
  }} data-sentry-element="unknown" data-sentry-component="SubscriptionPaymentProvider" data-sentry-source-file="SubscriptionPaymentProvider.tsx">
      {children}
    </SubscriptionPaymentContext.Provider>;
};
type Step = 1 | 2;
export type PaymentUpdateStatusType = 'RESERVED_GRADE' | 'SAME_GRADE' | 'UP_GRADE' | 'DOWN_GRADE' | 'NONE_UPGRADE';
type PaymentUpdateStatus = {
  type: PaymentUpdateStatusType;
  messages: string[];
  enabledSubmit: boolean;
};
export type PlanCheckoutContextProps = {
  subscription: SpacePlanSubscriptionDto;
  spaceHashId: string;
  selectedPlan: Omit<SubscribePlan, 'free'>;
  selectedPlanInfo: SelectedPlanInfo;
  showEnterpriseSample: boolean;
  selectedMaxCcu: number;
  step: Step;
  currency: Currency;
  추가결제금액?: {
    value: number;
    localString: string;
  };
  spacePlanProducts: SpacePlanProducts;
  handleSelectedPlan: (plan: Omit<SubscribePlan, 'free'>) => void;
  handleSelectedMaxCcu: (count: number) => void;
  handleIsUpdatedPayment: (isUpdated: boolean) => void;
  handleIsRegisterCard: (isRegisterCard: boolean) => void;
  handleSpaceBilling: (billingInfo: Partial<SpaceBillingInfo>) => void;
  handleStep: (step: Step) => void;
  // 기존 스페이스 데이터에 플랜이 잇어서 업데이트할려는 상황케이스
  isUpdated: boolean;
  isKR: boolean;
  // 결제 정보 변경
  isUpdatePayment: boolean;
  isSubmitLoading: boolean;
  isRegisterCard: boolean;
  space: SpaceSimpleDto;
  spacePlan?: SpacePlan;
  billingInfo: SpaceBillingInfo | null;
  updateStatus: PaymentUpdateStatus;
  amplitudeData: SubscriptionPaymentLogPayload;
  subscriptionPeriod: number;
  subscriptionRequestHashId?: string;
  handlePaymentPeriod: (period: number) => void;
  isAnnuallyPayment: boolean;
  // 연간 결제에만 유효
  annualSaleRate: number;
};
export type SubscriptionPaymentProviderProps = PropsWithChildren<{
  plan: Omit<SubscribePlan, 'free'>;
  maxCcu: number;
  spaceHashId: string;
  space: SpaceSimpleDto;
  billingInfo: SpaceBillingInfo | null;
  spacePlan: SpacePlanDto;
  spacePlanProducts: SpacePlanProducts;
  planInfo: SelectedPlanInfo;
  subscription: SpacePlanSubscriptionDto;
  showEnterpriseSample: boolean;
  isKR: boolean;
  isUpdated: boolean;
  subscriptionRequestHashId?: string;
  customSubscriptionRequestPriceInfo?: SpacePlanPaymentPriceInfo | null;
}>;
export type SelectedPlanInfo = Omit<PlanProduct, 'name' | 'status'> & {
  // 최종 금액
  finalPrice: number;
  finalPriceString: string;
  isSales: boolean;
  isAnnualSales: boolean;
  priceInfo?: SpacePlanPaymentPriceInfo | null;
};